import { propertyLib, validationLib } from "@agentcall/core/lib"
import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowLeft, Search } from "lucide-react"
import { Suspense, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router"
import { toast } from "sonner"
import { z } from "zod"
import { type PropertyModels, propertyApi, propertySchemas } from "@/api/client/property"
import { getErrorMessage } from "@/api/instance"
import {
  OwnerSearchModal,
  type SelectedOwner,
} from "@/components/owner/owner-search-modal/OwnerSearchModal"
import {
  PropertyStructureSelector,
  PropertyTypeSelector,
  PropertyYearSelector,
} from "@/components/property"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner/Spinner"
import { usePostalCodeSearch } from "@/hooks/usePostalCodeSearch"

const formSchema = propertySchemas.propertyUpdateBody.omit({ ownerId: true }).extend({
  name: z.string().min(1, "物件名を入力してください"),
  nameKana: z
    .string()
    .min(1, "物件名（カナ）を入力してください")
    .regex(validationLib.KATAKANA_REGEX, validationLib.KATAKANA_ERROR_MESSAGE),
  upperZip: z.string().regex(/^\d{3}$/, "3桁の数字を入力してください"),
  lowerZip: z.string().regex(/^\d{4}$/, "4桁の数字を入力してください"),
  prefecture: z.string().min(1, "都道府県を入力してください"),
  prefectureKana: z
    .string()
    .min(1, "都道府県（カナ）を入力してください")
    .regex(validationLib.KATAKANA_REGEX, validationLib.KATAKANA_ERROR_MESSAGE),
  city: z.string().min(1, "市区郡を入力してください"),
  cityKana: z
    .string()
    .min(1, "市区郡（カナ）を入力してください")
    .regex(validationLib.KATAKANA_REGEX, validationLib.KATAKANA_ERROR_MESSAGE),
  town: z.string().min(1, "町村を入力してください"),
  townKana: z
    .string()
    .min(1, "町村（カナ）を入力してください")
    .regex(validationLib.ADDRESS_KANA_REGEX, validationLib.ADDRESS_KANA_ERROR_MESSAGE),
  district: z.string().min(1, "町丁目を入力してください"),
  districtKana: z
    .string()
    .min(1, "町丁目（カナ）を入力してください")
    .regex(validationLib.ADDRESS_KANA_REGEX, validationLib.ADDRESS_KANA_ERROR_MESSAGE),
  managementCompanyName: z.string(),
  // フォームではstring型で扱うため、floor を変換
  floor: z
    .string()
    .min(1, "階数を入力してください")
    .refine((val) => !Number.isNaN(Number(val)), "階数は数値で入力してください"),
  // 電話番号のバリデーションを強化
  managementCompanyPhone: z
    .string()
    .refine(
      (value) => value === "" || /^\d{10,11}$/.test(value),
      "建物管理会社電話番号は10〜11桁の数字で入力してください"
    ),
})

type FormInputValues = z.input<typeof formSchema>

export const PropertyEditPage = () => {
  const { propertyId } = useParams<{ propertyId: string }>()
  const navigate = useNavigate()

  if (!propertyId) {
    return <div>物件IDが指定されていません</div>
  }

  return (
    <ReactErrorBoundary>
      <Suspense fallback={<PropertyEditPageFallback />}>
        <PropertyEditPageContent propertyId={propertyId} navigate={navigate} />
      </Suspense>
    </ReactErrorBoundary>
  )
}

interface PropertyEditPageContentProps {
  propertyId: string
  navigate: ReturnType<typeof useNavigate>
}

function PropertyEditPageContent({ propertyId, navigate }: PropertyEditPageContentProps) {
  const [isOwnerSearchModalOpen, setIsOwnerSearchModalOpen] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState<SelectedOwner | null>(null)
  const [isOwnerLoaded, setIsOwnerLoaded] = useState(false)

  const { data, refetch } = propertyApi.usePropertyGetSuspense(propertyId, {
    withOwner: "true",
    withRooms: "true",
    withRoomsTenants: "true",
    withRoomsPlans: "true",
  })
  const property = data.property

  // propertyUpdateのmutationを取得
  const { mutateAsync: updateProperty, isPending } = propertyApi.usePropertyUpdate({
    mutation: {
      onError: (error) =>
        toast.error("物件の更新に失敗しました", { description: getErrorMessage(error) }),
    },
  })

  const form = useForm<FormInputValues>({
    resolver: zodResolver(formSchema),
  })

  // APIから取得したデータでフォームを初期化
  useEffect(() => {
    form.reset({
      name: property.name,
      nameKana: property.nameKana,
      upperZip: property.upperZip,
      lowerZip: property.lowerZip,
      prefecture: property.prefecture,
      city: property.city,
      town: property.town,
      district: property.district,
      prefectureKana: property.prefectureKana,
      cityKana: property.cityKana,
      townKana: property.townKana,
      districtKana: property.districtKana,
      builtYear: property.builtYear,
      structure: property.structure,
      type: property.type,
      floor: String(property.floor),
      managementCompanyName: property.managementCompanyName,
      managementCompanyPhone: property.managementCompanyPhone,
      note: property.note,
    })

    if (property.owner) {
      setSelectedOwner({
        id: property.ownerId,
        name: `${property.owner.lastName} ${property.owner.firstName}`,
        nameKana:
          `${property.owner.lastNameKana || ""} ${property.owner.firstNameKana || ""}`.trim(),
        echoesOwnerId: property.owner.echoesOwnerId,
      })
    }
    setIsOwnerLoaded(true)
  }, [property, form])

  const { searchAddress, isSearching } = usePostalCodeSearch({
    onSuccess: (result) => {
      form.setValue("prefecture", result.prefecture ?? "")
      form.setValue("prefectureKana", result.prefectureKana ?? "")
      form.setValue("city", result.city ?? "")
      form.setValue("cityKana", result.cityKana ?? "")
      form.setValue("town", result.town ?? "")
      form.setValue("townKana", result.townKana ?? "")
      form.setValue("district", result.district ?? "")
      form.setValue("districtKana", result.districtKana ?? "")
      toast.success("住所を自動入力しました")
    },
    onError: (error) => {
      form.setError("upperZip", { message: error })
      toast.error(error)
    },
  })

  const handleSubmit = async (values: FormInputValues) => {
    try {
      const ownerId = selectedOwner?.id || property.ownerId
      if (!ownerId) {
        toast.error("オーナーが設定されていません")
        return
      }

      const data: PropertyModels.PropertyUpdateBody = {
        ownerId,
        name: values.name,
        nameKana: values.nameKana,
        upperZip: values.upperZip,
        lowerZip: values.lowerZip,
        prefecture: values.prefecture,
        city: values.city,
        town: values.town,
        district: values.district,
        prefectureKana: values.prefectureKana,
        cityKana: values.cityKana,
        townKana: values.townKana,
        districtKana: values.districtKana,
        builtYear: values.builtYear,
        structure: propertyLib.toStructure(values.structure),
        type: propertyLib.toType(values.type),
        floor: typeof values.floor === "string" ? Number(values.floor) : values.floor,
        managementCompanyName: values.managementCompanyName,
        managementCompanyPhone: values.managementCompanyPhone,
        note: values.note,
      }
      await updateProperty({ id: propertyId, data })
      await refetch()
      toast.success("物件を更新しました")
      navigate(`/properties/${propertyId}`)
    } catch {
      // 失敗の表示は mutation の onError が担当する
    }
  }

  const handleAddressSearch = async () => {
    const upperZip = form.getValues("upperZip")
    const lowerZip = form.getValues("lowerZip")
    await searchAddress(upperZip, lowerZip)
  }

  const handleOwnerSelect = (owner: SelectedOwner) => {
    setSelectedOwner(owner)
  }

  const handleSearchOwner = () => {
    setIsOwnerSearchModalOpen(true)
  }

  return (
    <div>
      {/* ヘッダーナビゲーション */}
      <div className="flex items-center gap-2 p-6">
        <button
          type="button"
          onClick={() => navigate(`/properties/${propertyId}`)}
          className="flex items-center justify-center w-7 h-7 bg-white rounded-[5px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]"
        >
          <ArrowLeft size={14} className="text-[#354267]" />
        </button>
        <span className="text-sm font-semibold text-[#3f4652]">物件詳細に戻る</span>
      </div>

      <div className="max-w-[895px] mx-auto px-6 pb-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* 物件概要カード */}
            <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
              <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                <h2 className="text-sm font-semibold text-[#3f4652]">物件概要</h2>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                {/* 物件名 */}
                <div className="grid grid-cols-3 gap-x-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          物件名
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="nameKana"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          物件名（カナ）
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* 郵便番号 */}
                <div>
                  <p className="text-xs font-semibold text-[#3f4652] mb-2">
                    郵便番号
                    <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                      必須
                    </span>
                  </p>
                  <div className="flex items-start gap-2">
                    <FormField
                      control={form.control}
                      name="upperZip"
                      render={({ field }) => (
                        <FormItem className="flex-none w-24">
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="123"
                              maxLength={3}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, "")
                                field.onChange(value)
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <span className="text-lg leading-9">-</span>
                    <FormField
                      control={form.control}
                      name="lowerZip"
                      render={({ field }) => (
                        <FormItem className="flex-none w-32">
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="4567"
                              maxLength={4}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, "")
                                field.onChange(value)
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddressSearch}
                      disabled={isSearching}
                      className="h-8 py-2"
                    >
                      {isSearching ? "検索中..." : "住所検索"}
                    </Button>
                  </div>
                </div>

                {/* 住所 */}
                <div className="grid grid-cols-4 gap-x-4">
                  <FormField
                    control={form.control}
                    name="prefecture"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          都道府県
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="選択してください" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          市区郡
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="town"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          町村
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="district"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          町丁目
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* 住所（カナ） */}
                <div className="grid grid-cols-4 gap-x-4">
                  <FormField
                    control={form.control}
                    name="prefectureKana"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          都道府県（カナ）
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cityKana"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          市区郡（カナ）
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="townKana"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          町村（カナ）
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="districtKana"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          町丁目（カナ）
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* 築年・構造・用法 */}
                <div className="grid grid-cols-3 gap-x-4">
                  <FormField
                    control={form.control}
                    name="builtYear"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          築年
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <PropertyYearSelector
                            value={field.value}
                            onChange={field.onChange}
                            error={fieldState.error?.message}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="structure"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          構造
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <PropertyStructureSelector
                            value={field.value}
                            onChange={field.onChange}
                            error={fieldState.error?.message}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          用法
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <PropertyTypeSelector
                            value={field.value}
                            onChange={field.onChange}
                            error={fieldState.error?.message}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                {/* 階数 */}
                <div className="grid grid-cols-3 gap-x-4">
                  <FormField
                    control={form.control}
                    name="floor"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          階数
                          <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                            必須
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="2" type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* 建物管理会社 */}
                <div className="grid grid-cols-3 gap-x-4">
                  <FormField
                    control={form.control}
                    name="managementCompanyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          建物管理会社
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="managementCompanyPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold text-[#3f4652]">
                          建物管理会社電話番号（ハイフンなし）
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="01201234567"
                            maxLength={11}
                            onChange={(e) => {
                              // 数字のみを許可
                              const value = e.target.value.replace(/[^0-9]/g, "")
                              field.onChange(value)
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* オーナー情報カード */}
            <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
              <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-[#3f4652]">オーナー情報</h2>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSearchOwner}
                    className="h-8"
                  >
                    <Search size={14} />
                    オーナー検索
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-5">
                {selectedOwner ? (
                  <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                    <div>
                      <p className="text-xs font-medium text-[#9eafc7] mb-2">オーナーID</p>
                      <p className="text-sm text-[#3f4652]">{selectedOwner.id}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#9eafc7] mb-2">オーナー名</p>
                      <p className="text-sm text-[#3f4652]">{selectedOwner.name}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#9eafc7] mb-2">オーナー名（カナ）</p>
                      <p className="text-sm text-[#3f4652]">{selectedOwner.nameKana}</p>
                    </div>
                  </div>
                ) : isOwnerLoaded ? (
                  <p className="text-sm text-slate-400 text-center py-8">
                    オーナー情報が設定されていません
                  </p>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">
                    オーナー情報を読み込んでいます...
                  </p>
                )}
              </CardContent>
            </Card>

            {/* 物件注意事項カード */}
            <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
              <CardHeader className="border-b border-slate-200 py-2.5 px-5">
                <h2 className="text-sm font-semibold text-[#3f4652]">物件注意事項</h2>
              </CardHeader>
              <CardContent className="p-5">
                <FormField
                  control={form.control}
                  name="note"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <textarea
                          {...field}
                          className="w-full min-h-[120px] px-3 py-2 text-sm bg-white border border-slate-300 rounded-[5px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* 送信ボタン */}
            <div className="flex justify-end gap-4">
              <Button
                type="submit"
                variant="default"
                className="min-w-[100px]"
                disabled={isPending}
              >
                {isPending ? "更新中..." : "保存する"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/properties/${propertyId}`)}
                disabled={isPending}
              >
                キャンセル
              </Button>
            </div>
          </form>
        </Form>
      </div>

      <OwnerSearchModal
        open={isOwnerSearchModalOpen}
        onOpenChange={setIsOwnerSearchModalOpen}
        onSelect={handleOwnerSelect}
      />
    </div>
  )
}

function PropertyEditPageFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className="text-slate-600 size-10 transition-opacity" />
    </div>
  )
}
